import { getPlatformLabel } from '@/components/shared/platform/platform-utils';

/**
 * Assistants whose Kirin endpoint hook denies MCP tool calls to a server
 * blocked from Inventory (KIR-4482). Other sources (Cursor, VS Code, Claude
 * Desktop, ...) still list the server in Inventory but run it unblocked.
 * Keep in step with the agent platforms that route PreMCPToolUse through the
 * MCP server block processor.
 */
export const HOOK_ENFORCED_SOURCES: readonly string[] = [
  'claude',
  'codex',
  'github_copilot_cli',
  'windsurf',
];

/** Where enforcement on an assistant has a precondition, say so. */
const ENFORCEMENT_CAVEATS: Readonly<Record<string, string>> = {
  codex: 'when Codex support is enabled for the organization',
  github_copilot_cli: 'when the server can be matched to its configuration',
};

/** "A", "A and B", "A, B, and C". */
export function formatList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function label(source: string): string {
  const caveat = ENFORCEMENT_CAVEATS[source];
  const name = getPlatformLabel(source);
  return caveat ? `${name} (${caveat})` : name;
}

/**
 * Split the assistants a server was seen in into enforced and not enforced,
 * as display labels (de-duplicated). A server not yet seen in any enforced
 * assistant names them all: the block applies wherever it shows up next.
 */
export function splitEnforcedAssistants(sources: readonly string[]): {
  enforced: string[];
  notEnforced: string[];
} {
  const unique = [...new Set(sources)];
  const seenEnforced = unique.filter(s => HOOK_ENFORCED_SOURCES.includes(s));
  const enforcedSources =
    seenEnforced.length > 0 ? seenEnforced : [...HOOK_ENFORCED_SOURCES];
  return {
    enforced: [...new Set(enforcedSources.map(label))],
    notEnforced: [
      ...new Set(
        unique
          .filter(s => !HOOK_ENFORCED_SOURCES.includes(s))
          .map(getPlatformLabel)
      ),
    ],
  };
}
